import React, { useEffect, useState } from "react";
import { PhoneIncoming, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePowerDialer } from "@/context/PowerDialerContext";

export function ReturnCallBanner() {
  const { returnCall, takeReturnCall, hangUpReturnCall, isReturnCallActionPending } = usePowerDialer();
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const expiresAt = returnCall?.status === "RINGING" ? returnCall.expiresAt : null;

  useEffect(() => {
    if (!expiresAt) {
      setSecondsLeft(null);
      return;
    }
    const expiresAtMillis = new Date(expiresAt).getTime();
    const updateSecondsLeft = () => setSecondsLeft(Math.max(0, Math.ceil((expiresAtMillis - Date.now()) / 1000)));
    updateSecondsLeft();
    const timer = setInterval(updateSecondsLeft, 1000);
    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!returnCall) {
    return null;
  }

  const caller = returnCall.leadName ?? returnCall.phoneNumber;
  const details = [
    returnCall.campaignName,
    returnCall.lastDisposition ? `Last disposition: ${returnCall.lastDisposition}` : null,
  ]
    .filter(Boolean)
    .join(". ");

  if (returnCall.status === "CONNECTED") {
    return (
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border px-6 py-2.5">
        <div className="flex min-w-0 items-center gap-3">
          <span className="status-pill status-pill-success gap-1.5 px-3 py-1 text-sm">
            <span className="h-2 w-2 shrink-0 rounded-full bg-current animate-pulse" />
            On a call with {caller}
          </span>
          <span className="truncate text-sm text-muted-foreground">
            {details ? `${details}. ` : ""}Dialing is paused until you press Ready.
          </span>
        </div>
        <Button
          size="icon"
          variant="destructive"
          className="h-11 w-11 rounded-full text-white"
          title="Hang up"
          onClick={() => void hangUpReturnCall()}
          disabled={isReturnCallActionPending}
        >
          <PhoneOff className="h-5 w-5" />
        </Button>
      </div>
    );
  }

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-border bg-info-soft px-6 py-3 text-info-soft-foreground">
      <div className="flex min-w-0 items-center gap-3">
        <PhoneIncoming className="h-5 w-5 shrink-0" />
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold">{caller} is calling you back</p>
          <p className="truncate text-xs">
            {details ? `${details}. ` : ""}Taking it pauses dialing.
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3">
        {secondsLeft !== null && (
          <span className="text-xs tabular-nums">Goes to your call flow in {secondsLeft}s</span>
        )}
        <Button size="sm" onClick={() => void takeReturnCall()} disabled={isReturnCallActionPending}>
          <PhoneIncoming className="mr-2 h-4 w-4" />
          Take call
        </Button>
      </div>
    </div>
  );
}
